if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileZoomMediaQuery = window.matchMedia('(max-width: 749px)');
        this.mobileZoomMin = 1;
        this.mobileZoomMax = 4;
        this.mobileZoomScale = 1;
        this.lastMobileTapAt = 0;
        this.currentMobileMediaIndex = -1;
        this.mobileSwipeThreshold = 48;
        this.mobileSwipeRestraint = 1.25;
        this.onMobileImageClick = this.onMobileImageClick.bind(this);
        this.onMobileZoomTouchStart = this.onMobileZoomTouchStart.bind(this);
        this.onMobileZoomTouchMove = this.onMobileZoomTouchMove.bind(this);
        this.onMobileZoomTouchEnd = this.onMobileZoomTouchEnd.bind(this);

        this.galleryControls = this.querySelector('.product-media-modal__mobile-gallery-controls');
        this.galleryControls?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.galleryControls?.addEventListener('click', (event) => event.stopPropagation());
        this.querySelector('[role="document"]')?.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse') event.stopPropagation();
        });
        this.galleryButtons = this.querySelectorAll('[data-modal-gallery]');
        this.galleryButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.handleMobileGalleryControl(button.dataset.modalGallery);
          });
        });
      }

      hide() {
        const activeMedia =
          this.activeMobileImage || this.querySelector('.product-media-modal__content > img[data-media-id].active');
        if (this.isImageMedia(activeMedia)) this.syncProductGallery(activeMedia);
        this.resetMobileZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.resetMobileZoom();
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        this.setActiveModalMedia(activeMedia);

        const container = this.querySelector('[role="document"]');
        if (this.isMobileImage(activeMedia)) {
          this.prepareMobileImageZoom(activeMedia, container);
        } else if (this.isImageMedia(activeMedia)) {
          this.prepareDesktopImageGallery(activeMedia, container);
        } else {
          this.toggleGalleryControls(false);
          activeMedia.scrollIntoView();
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      isMobileImage(media) {
        return this.mobileZoomMediaQuery.matches && media?.nodeName === 'IMG';
      }

      isImageMedia(media) {
        return media?.nodeName === 'IMG';
      }

      prepareDesktopImageGallery(image, container) {
        if (!container) return;
        this.mobileImageMedia = this.getMobileImageMedia();
        this.currentMobileMediaIndex = this.mobileImageMedia.indexOf(image);
        this.activeMobileImage = image;
        this.mobileZoomContainer = container;
        this.toggleGalleryControls(true);
        this.setMobileZoom(1);

        window.requestAnimationFrame(() => {
          container.scrollLeft = 0;
          container.scrollTop = 0;
        });
      }

      prepareMobileImageZoom(image, container) {
        if (!container) return;
        this.mobileImageMedia = this.getMobileImageMedia();
        this.currentMobileMediaIndex = this.mobileImageMedia.indexOf(image);
        this.activeMobileImage = image;
        this.mobileZoomContainer = container;
        this.toggleGalleryControls(true);
        image.classList.add('product-media-modal__image--mobile-zoom');
        image.addEventListener('click', this.onMobileImageClick);
        container.addEventListener('touchstart', this.onMobileZoomTouchStart, { passive: false });
        container.addEventListener('touchmove', this.onMobileZoomTouchMove, { passive: false });
        container.addEventListener('touchend', this.onMobileZoomTouchEnd);
        container.addEventListener('touchcancel', this.onMobileZoomTouchEnd);
        this.setMobileZoom(1);

        window.requestAnimationFrame(() => {
          container.scrollLeft = 0;
          container.scrollTop = 0;
        });
      }

      handleMobileGalleryControl(action) {
        const media = this.mobileImageMedia || this.getMobileImageMedia();
        if (!media.length || this.currentMobileMediaIndex < 0) return;

        const direction = action === 'previous' ? -1 : 1;
        const nextIndex = (this.currentMobileMediaIndex + direction + media.length) % media.length;
        this.showMobileMediaAt(nextIndex, this.mobileZoomScale);
      }

      onMobileImageClick(event) {
        if (!this.activeMobileImage || event.currentTarget !== this.activeMobileImage) return;
        const now = Date.now();

        if (now - this.lastMobileTapAt < 320) {
          event.preventDefault();
          this.setMobileZoom(this.mobileZoomScale > 1 ? 1 : 2);
        }

        this.lastMobileTapAt = now;
      }

      onMobileZoomTouchStart(event) {
        if (!this.activeMobileImage) return;

        if (event.touches.length === 1) {
          this.touchZoomStartDistance = null;
          this.touchZoomStartScale = this.mobileZoomScale;

          if (this.mobileZoomScale <= 1) {
            const touch = event.touches[0];
            this.touchSwipeStartX = touch.clientX;
            this.touchSwipeStartY = touch.clientY;
            this.hasHandledMobileSwipe = false;
          } else {
            this.clearMobileSwipe();
          }
          return;
        }

        if (event.touches.length !== 2) return;

        this.clearMobileSwipe();
        this.touchZoomStartDistance = this.getTouchDistance(event.touches);
        this.touchZoomStartScale = this.mobileZoomScale;
      }

      onMobileZoomTouchMove(event) {
        if (event.touches.length === 1) {
          this.onMobileSwipeTouchMove(event);
          return;
        }

        if (!this.touchZoomStartDistance || event.touches.length !== 2) return;
        event.preventDefault();

        const distance = this.getTouchDistance(event.touches);
        const zoomRatio = distance / this.touchZoomStartDistance;
        this.setMobileZoom(this.touchZoomStartScale * zoomRatio);
      }

      onMobileZoomTouchEnd(event) {
        if (event.touches.length >= 2) {
          this.clearMobileSwipe();
          return;
        }

        this.onMobileSwipeTouchEnd(event);

        this.touchZoomStartDistance = null;
        this.touchZoomStartScale = this.mobileZoomScale;
      }

      getTouchDistance(touches) {
        const x = touches[0].clientX - touches[1].clientX;
        const y = touches[0].clientY - touches[1].clientY;
        return Math.hypot(x, y);
      }

      onMobileSwipeTouchMove(event) {
        if (!this.activeMobileImage || this.mobileZoomScale > 1 || this.touchZoomStartDistance) return;

        if (this.touchSwipeStartX == null) {
          const touch = event.touches[0];
          this.touchSwipeStartX = touch.clientX;
          this.touchSwipeStartY = touch.clientY;
          this.hasHandledMobileSwipe = false;
          return;
        }

        const touch = event.touches[0];
        const deltaX = touch.clientX - this.touchSwipeStartX;
        const deltaY = touch.clientY - this.touchSwipeStartY;

        if (Math.abs(deltaX) > 16 && Math.abs(deltaX) > Math.abs(deltaY) * this.mobileSwipeRestraint) {
          event.preventDefault();
        }
      }

      onMobileSwipeTouchEnd(event) {
        if (
          !this.activeMobileImage ||
          this.mobileZoomScale > 1 ||
          this.touchSwipeStartX == null ||
          this.hasHandledMobileSwipe
        ) {
          this.clearMobileSwipe();
          return;
        }

        const touch = event.changedTouches?.[0];
        if (!touch) {
          this.clearMobileSwipe();
          return;
        }

        const deltaX = touch.clientX - this.touchSwipeStartX;
        const deltaY = touch.clientY - this.touchSwipeStartY;
        const isHorizontalSwipe =
          Math.abs(deltaX) >= this.mobileSwipeThreshold &&
          Math.abs(deltaX) > Math.abs(deltaY) * this.mobileSwipeRestraint;

        if (isHorizontalSwipe) {
          event.preventDefault();
          this.hasHandledMobileSwipe = true;
          this.handleMobileGalleryControl(deltaX < 0 ? 'next' : 'previous');
        }

        this.clearMobileSwipe();
      }

      clearMobileSwipe() {
        this.touchSwipeStartX = null;
        this.touchSwipeStartY = null;
        this.hasHandledMobileSwipe = false;
      }

      setMobileZoom(scale) {
        const image = this.activeMobileImage;
        const container = this.mobileZoomContainer;
        if (!image || !container) return;

        const nextScale = Math.min(Math.max(scale, this.mobileZoomMin), this.mobileZoomMax);
        const scrollCenter = this.getScrollCenter(container);

        if (nextScale === 1) {
          this.mobileZoomScale = 1;
          container.classList.remove('is-mobile-zoomed');
          image.classList.remove('is-mobile-zoomed');
          image.style.removeProperty('width');
          image.style.removeProperty('height');
          image.style.removeProperty('max-width');
          image.style.removeProperty('max-height');
          image.style.removeProperty('--mobile-zoom-width');
          delete image.dataset.mobileZoomBaseWidth;
          this.updateGalleryButtons();

          window.requestAnimationFrame(() => {
            container.scrollLeft = 0;
            container.scrollTop = 0;
          });
          return;
        }

        if (!image.dataset.mobileZoomBaseWidth || this.mobileZoomScale === 1) {
          image.style.removeProperty('width');
          image.style.removeProperty('height');
          image.style.removeProperty('max-width');
          image.style.removeProperty('max-height');
          image.dataset.mobileZoomBaseWidth = this.getMobileZoomBaseWidth(image, container);
        }

        container.classList.add('is-mobile-zoomed');
        image.classList.add('is-mobile-zoomed');
        image.style.setProperty('--mobile-zoom-width', `${Number(image.dataset.mobileZoomBaseWidth) * nextScale}px`);
        image.style.width = 'var(--mobile-zoom-width)';
        image.style.height = 'auto';
        image.style.maxWidth = 'none';
        image.style.maxHeight = 'none';
        this.mobileZoomScale = nextScale;
        this.updateGalleryButtons();

        window.requestAnimationFrame(() => {
          container.scrollLeft = Math.max(0, container.scrollWidth * scrollCenter.x - container.clientWidth / 2);
          container.scrollTop = Math.max(0, container.scrollHeight * scrollCenter.y - container.clientHeight / 2);
        });
      }

      getScrollCenter(container) {
        return {
          x: container.scrollWidth
            ? (container.scrollLeft + container.clientWidth / 2) / container.scrollWidth
            : 0.5,
          y: container.scrollHeight
            ? (container.scrollTop + container.clientHeight / 2) / container.scrollHeight
            : 0.5,
        };
      }

      getMobileZoomBaseWidth(image, container) {
        const rectWidth = image.getBoundingClientRect().width;
        if (rectWidth > 1) return rectWidth;

        const containerStyles = window.getComputedStyle(container);
        const horizontalPadding =
          parseFloat(containerStyles.paddingLeft || 0) + parseFloat(containerStyles.paddingRight || 0);
        const verticalPadding =
          parseFloat(containerStyles.paddingTop || 0) + parseFloat(containerStyles.paddingBottom || 0);
        const availableWidth = Math.max(container.clientWidth - horizontalPadding, 1);
        const availableHeight = Math.max(container.clientHeight - verticalPadding, 1);
        const imageWidth = image.naturalWidth || Number(image.getAttribute('width'));
        const imageHeight = image.naturalHeight || Number(image.getAttribute('height'));

        if (!imageWidth || !imageHeight) return availableWidth;

        return Math.max(Math.min(availableWidth, availableHeight * (imageWidth / imageHeight)), 1);
      }

      getMobileImageMedia() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > img[data-media-id]'));
      }

      showMobileMediaAt(index, preferredScale = 1) {
        const media = this.mobileImageMedia || this.getMobileImageMedia();
        const nextMedia = media[index];
        if (!nextMedia || !this.mobileZoomContainer) return;

        const container = this.mobileZoomContainer;
        this.resetMobileZoom({ keepControls: true });
        this.setActiveModalMedia(nextMedia);
        this.mobileImageMedia = media;
        this.currentMobileMediaIndex = index;
        if (this.isMobileImage(nextMedia)) {
          this.prepareMobileImageZoom(nextMedia, container);
        } else {
          this.prepareDesktopImageGallery(nextMedia, container);
        }
        this.syncProductGallery(nextMedia);

        if (this.isMobileImage(nextMedia) && preferredScale > 1) {
          window.requestAnimationFrame(() => this.setMobileZoom(preferredScale));
        }
      }

      setActiveModalMedia(activeMedia) {
        this.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
          if (element !== activeMedia && this.isImageMedia(element)) this.resetImageZoomState(element);
        });
      }

      syncProductGallery(media) {
        if (!media?.dataset?.mediaId || !this.dataset.section) return;

        const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        const sectionMediaId = `${this.dataset.section}-${media.dataset.mediaId}`;
        mediaGallery?.setActiveMedia?.(sectionMediaId, false);
        this.updateModalCloseFocus(mediaGallery, sectionMediaId, media.dataset.mediaId);
        this.syncProductGallerySlider(mediaGallery, sectionMediaId);

        window.setTimeout(() => {
          this.syncProductGallerySlider(mediaGallery, sectionMediaId);
        }, 120);
      }

      updateModalCloseFocus(mediaGallery, sectionMediaId, mediaId) {
        const activeSlide = mediaGallery?.querySelector(`[data-media-id="${sectionMediaId}"]`);
        const activeOpener = activeSlide?.querySelector(`button[data-media-id="${mediaId}"]`);
        if (activeOpener) this.openedBy = activeOpener;
      }

      syncProductGallerySlider(mediaGallery, sectionMediaId) {
        const viewer = mediaGallery?.elements?.viewer || mediaGallery?.querySelector('[id^="GalleryViewer"]');
        const activeSlide = viewer?.querySelector(`[data-media-id="${sectionMediaId}"]`);
        const slider = viewer?.slider || viewer?.querySelector('[id^="Slider-"]');
        if (!viewer || !activeSlide || !slider) return;

        if (!Number.isFinite(viewer.sliderItemOffset) || viewer.sliderItemOffset <= 0) {
          this.updateProductGalleryCounter(viewer, activeSlide, slider);
          return;
        }

        slider.scrollTo({ left: activeSlide.offsetLeft });
        viewer.update?.();

        window.requestAnimationFrame(() => {
          viewer.update?.();
        });
      }

      updateProductGalleryCounter(viewer, activeSlide, slider) {
        const slides = Array.from(viewer.sliderItems?.length ? viewer.sliderItems : slider.querySelectorAll('[id^="Slide-"]'));
        const activeIndex = slides.indexOf(activeSlide);
        if (activeIndex < 0) return;

        viewer.currentPage = activeIndex + 1;
        viewer.totalPages = slides.length;

        if (viewer.currentPageElement) viewer.currentPageElement.textContent = viewer.currentPage;
        if (viewer.pageTotalElement) viewer.pageTotalElement.textContent = viewer.totalPages;

        viewer.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: viewer.currentPage,
              currentElement: activeSlide,
            },
          })
        );
      }

      resetMobileZoom(options = {}) {
        if (this.mobileZoomContainer) {
          this.mobileZoomContainer.classList.remove('is-mobile-zoomed');
          this.mobileZoomContainer.removeEventListener('touchstart', this.onMobileZoomTouchStart);
          this.mobileZoomContainer.removeEventListener('touchmove', this.onMobileZoomTouchMove);
          this.mobileZoomContainer.removeEventListener('touchend', this.onMobileZoomTouchEnd);
          this.mobileZoomContainer.removeEventListener('touchcancel', this.onMobileZoomTouchEnd);
        }

        if (this.activeMobileImage) {
          this.activeMobileImage.removeEventListener('click', this.onMobileImageClick);
          this.resetImageZoomState(this.activeMobileImage);
        }

        this.activeMobileImage = null;
        this.mobileZoomContainer = null;
        this.touchZoomStartDistance = null;
        this.touchZoomStartScale = 1;
        this.clearMobileSwipe();
        this.mobileZoomScale = 1;
        if (!options.keepControls) {
          this.mobileImageMedia = null;
          this.currentMobileMediaIndex = -1;
          this.toggleGalleryControls(false);
        }
      }

      resetImageZoomState(image) {
        image.classList.remove('product-media-modal__image--mobile-zoom', 'is-mobile-zoomed');
        image.style.removeProperty('width');
        image.style.removeProperty('height');
        image.style.removeProperty('max-width');
        image.style.removeProperty('max-height');
        image.style.removeProperty('--mobile-zoom-width');
        delete image.dataset.mobileZoomBaseWidth;
      }

      toggleGalleryControls(show) {
        if (!this.galleryControls) return;

        this.galleryControls.hidden = !show;
        this.updateGalleryButtons();
      }

      updateGalleryButtons() {
        const media = this.mobileImageMedia || this.getMobileImageMedia();
        this.galleryButtons?.forEach((button) => {
          button.disabled = media.length < 2;
        });
      }
    }
  );
}
